import apiService, { ServiceType } from './api';

const coreService = apiService.getService(ServiceType.CORE);

export interface BoardSymbol {
    symbol: string;
    exchange: string;
    kind: 'STOCK' | 'FUTURE';
    /** Futures only: 1M, 2M, 1Q, 2Q. */
    tenor?: string;
}

export interface BoardOrder {
    id: string;
    portfolioId: string;
    account: string;
    clientOrderId: string;
    exchange: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    orderType: 'MARKET' | 'LIMIT';
    quantity: number;
    /** Raw VND for a stock, index points for a future. */
    price: number;
    route: 'SERVER' | 'CLIENT';
    status: string;
    cancelStatus: string | null;
    matchedQuantity: number;
    avgPrice: number | null;
    rejectText: string | null;
    open: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface PlaceBoardOrder {
    portfolioId: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    orderType: 'MARKET' | 'LIMIT';
    quantity: number;
    /** LIMIT only: raw VND for a stock, index points for a future. */
    price?: number;
}

export interface BrokerOrder {
    exchangeOrderId: string | null;
    clientOrderId: string | null;
    symbol: string;
    side: 'BUY' | 'SELL';
    /** Raw VND for a stock, index points for a future; null for a market order. */
    price: number | null;
    quantity: number;
    filledQuantity: number;
    status: string;
    owner: 'BOARD' | 'GRID' | 'PAIR' | 'SMART_BETA' | 'UNKNOWN';
    ownerRunning: boolean;
    cancellable: boolean;
    /** Why it cannot be cancelled. */
    reason: string | null;
}

export interface BrokerOrders {
    route: 'SERVER' | 'CLIENT';
    /** False: the broker's order list is unreadable, so these are what Fortuna recorded today. */
    brokerListed: boolean;
    orders: BrokerOrder[];
}

export const priceBoardApi = {
    async vn30(): Promise<{ asOf: string | null; symbols: BoardSymbol[] }> {
        return (await coreService.get('/price-board/vn30')).data;
    },
    async futures(): Promise<BoardSymbol[]> {
        return (await coreService.get('/price-board/futures')).data;
    },
    async search(q: string): Promise<BoardSymbol[]> {
        return (await coreService.get('/price-board/search', { params: { q } })).data;
    },
    async orders(): Promise<BoardOrder[]> {
        return (await coreService.get('/price-board/orders')).data;
    },
    async place(order: PlaceBoardOrder): Promise<BoardOrder> {
        return (await coreService.post('/price-board/orders', order)).data;
    },
    async cancel(id: string): Promise<BoardOrder> {
        return (await coreService.post(`/price-board/orders/${id}/cancel`)).data;
    },
    async brokerOrders(portfolioId: string): Promise<BrokerOrders> {
        return (await coreService.get('/price-board/broker-orders', { params: { portfolioId } })).data;
    },
    async cancelBrokerOrder(portfolioId: string, exchangeOrderId: string): Promise<{ requested: true }> {
        return (await coreService.post('/price-board/broker-orders/cancel', { portfolioId, exchangeOrderId })).data;
    },
};

/** The server's reason, or a generic line. */
export const boardError = (e: any, fallback: string): string => {
    const m = e?.response?.data?.message;
    return Array.isArray(m) ? m.join('; ') : m || fallback;
};
